import { handleActions } from 'redux-actions';
import { defaultTwoFactorAuthWidgetState } from './defaults';
import { TwoFactorAuthWidgetData, TwoFactorAuthWidgetStep } from './types';
import { initSecurityPage } from '../../actions';
import {
  submitTwoFactorAuthWidgetStart,
  submitTwoFactorAuthWidgetStartFailure,
  submitTwoFactorAuthWidgetStartSuccess,
  submitTwoFactorAuthWidgetVerifyAuthenticatorFailure,
  submitTwoFactorAuthWidgetVerifyAuthenticatorSuccess,
  submitTwoFactorAuthWidgetRegenerateRecoveryCodesSuccess,
  submitTwoFactorAuthWidgetRegenerateRecoveryCodesFailure,
  submitTwoFactorAuthWidgetResetFailure,
  submitTwoFactorAuthWidgetRegenerateRecoveryCodes,
  submitTwoFactorAuthWidgetReset,
  submitTwoFactorAuthWidgetResetSuccess,
  submitTwoFactorAuthWidgetVerifyAuthenticator,
  submitTwoFactorAuthWidgetCancel,
  submitTwoFactorAuthWidgetCancelSuccess,
  submitTwoFactorAuthWidgetCancelFailure,
} from './actions';
import { NetworkRequestState } from '../../../networking/types';
import { networkRequestMetaReducer } from '../../../networking/utils';

export default handleActions<TwoFactorAuthWidgetData, any>(
  {
    [initSecurityPage.toString()]: (state, action: ReturnType<typeof initSecurityPage>) => {
      const twoFactorAuthentication = action.payload.data.twoFactorAuthentication;
      if (!twoFactorAuthentication) {
        return state;
      }

      let step = TwoFactorAuthWidgetStep.NotEnabled;
      if (twoFactorAuthentication.enabled && twoFactorAuthentication.confirmed) {
        step = TwoFactorAuthWidgetStep.Enabled;
      } else if (twoFactorAuthentication.enabled && !twoFactorAuthentication.confirmed) {
        step = TwoFactorAuthWidgetStep.AttachAuthenticator;
      }

      return {
        ...state,
        step: step,
        canEnable: twoFactorAuthentication?.canEnable,
        enabled: twoFactorAuthentication?.enabled,
        confirmed: twoFactorAuthentication?.confirmed,
        secretUrl: twoFactorAuthentication?.secretUrl,
        secret: twoFactorAuthentication?.secret,
        request: {
          status: NetworkRequestState.IDLE,
          errors: {},
        },
      };
    },
    ...networkRequestMetaReducer({
      initialAction: submitTwoFactorAuthWidgetStart,
      successAction: submitTwoFactorAuthWidgetStartSuccess,
      failureAction: submitTwoFactorAuthWidgetStartFailure,
      additionalStateUpdates: {
        successStateUpdate: (state, action): Partial<TwoFactorAuthWidgetData> => ({
          step: TwoFactorAuthWidgetStep.AttachAuthenticator,
          secretUrl: action.payload.secretUrl,
          secret: action.payload.secret,
        }),
      },
    }),
    ...networkRequestMetaReducer({
      initialAction: submitTwoFactorAuthWidgetVerifyAuthenticator,
      successAction: submitTwoFactorAuthWidgetVerifyAuthenticatorSuccess,
      failureAction: submitTwoFactorAuthWidgetVerifyAuthenticatorFailure,
      additionalStateUpdates: {
        successStateUpdate: (state, action): Partial<TwoFactorAuthWidgetData> => ({
          step: TwoFactorAuthWidgetStep.SuccessWithRecoveryCodes,
          recoveryCodes: action.payload.recoveryCodes,
        }),
      },
    }),
    ...networkRequestMetaReducer({
      initialAction: submitTwoFactorAuthWidgetRegenerateRecoveryCodes,
      successAction: submitTwoFactorAuthWidgetRegenerateRecoveryCodesSuccess,
      failureAction: submitTwoFactorAuthWidgetRegenerateRecoveryCodesFailure,
      additionalStateUpdates: {
        successStateUpdate: (state, action): Partial<TwoFactorAuthWidgetData> => ({
          step: TwoFactorAuthWidgetStep.SuccessWithRecoveryCodes,
          recoveryCodes: action.payload.recoveryCodes,
        }),
      },
    }),
    ...networkRequestMetaReducer({
      initialAction: submitTwoFactorAuthWidgetReset,
      successAction: submitTwoFactorAuthWidgetResetSuccess,
      failureAction: submitTwoFactorAuthWidgetResetFailure,
      additionalStateUpdates: {
        successStateUpdate: (state, action): Partial<TwoFactorAuthWidgetData> => ({
          ...state,
          step: TwoFactorAuthWidgetStep.NotEnabled,
        }),
      },
    }),
    ...networkRequestMetaReducer({
      initialAction: submitTwoFactorAuthWidgetCancel,
      successAction: submitTwoFactorAuthWidgetCancelSuccess,
      failureAction: submitTwoFactorAuthWidgetCancelFailure,
      additionalStateUpdates: {
        successStateUpdate: (state, action): Partial<TwoFactorAuthWidgetData> => ({
          ...state,
          step: TwoFactorAuthWidgetStep.NotEnabled,
        }),
      },
    }),
  },
  defaultTwoFactorAuthWidgetState
);
